// SocketContext.jsx — Complete updated version with read receipts
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef
} from 'react';
import { io } from 'socket.io-client';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [toastNotification, setToastNotification] = useState(null);
  const [loading, setLoading] = useState(false);

  // Chat state
  const [chatMessagesByGroup, setChatMessagesByGroup] = useState({});
  const [chatUnreadByGroup, setChatUnreadByGroup] = useState({});

  const socketRef = useRef(null);

  // Get user data from localStorage
  const getUserData = useCallback(() => {
    try {
      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) return JSON.parse(userDataStr);

      const userLabId = localStorage.getItem('userLabId');
      const labName = localStorage.getItem('labName');
      const labId = localStorage.getItem('labId');
      const userId = localStorage.getItem('userId');
      const userName = localStorage.getItem('userName');

      if (userLabId || labId) {
        return {
          userLabId: userLabId || labId,
          labId: labId || userLabId,
          labName: labName || 'Unknown Lab',
          userId: userId || '',
          userName: userName || 'Unknown User'
        };
      }
      return {};
    } catch {
      return {};
    }
  }, []);

  const showToast = useCallback((notification) => {
    setToastNotification(notification);
    setTimeout(() => setToastNotification(null), 5000);
  }, []);

  // Initialize socket connection
  useEffect(() => {
    const currentOrigin = window.location.origin;
    const token = localStorage.getItem('accessToken') || '';

    const newSocket = io('https://hms.backend.hiragroupcloud.com', {
      path: '/socket.io/',
      transports: ['polling', 'websocket'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      autoConnect: true,
      auth: {
        origin: currentOrigin,
        token
      }
    });

    socketRef.current = newSocket;

    newSocket.on('connect', () => {
      setIsConnected(true);

      const userData = getUserData();
      let userLabId = userData.userLabId || userData.labId || '';
      if (!userLabId) {
        userLabId =
          localStorage.getItem('userLabId') ||
          localStorage.getItem('labId') ||
          localStorage.getItem('user_lab_id') ||
          '';
      }

      const labName =
        userData.labName ||
        localStorage.getItem('labName') ||
        localStorage.getItem('currentLabName') ||
        'Unknown Lab';

      const firstName = userData.first_name || userData.firstName || '';
      const lastName = userData.last_name || userData.lastName || '';
      const userName =
        `${firstName} ${lastName}`.trim() ||
        userData.username ||
        userData.userName ||
        'Unknown User';

      const userId =
        userData.userId ||
        userData._id ||
        userData.id ||
        localStorage.getItem('userId') ||
        '';

      // Existing outsource registration — untouched
      if (userLabId) {
        newSocket.emit('register', {
          userLabId: String(userLabId),
          userId: String(userId),
          labName: String(labName),
          userName: String(userName)
        });
      }
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
      setIsRegistered(false);
    });

    newSocket.on('connect_error', () => {
      setIsConnected(false);
    });

    newSocket.on('registered', (data) => {
      setIsRegistered(!!data?.success);
    });

    // ============ EXISTING OUTSOURCE NOTIFICATION HANDLER (untouched) ============
    newSocket.on('new-notification', (notification) => {
      setNotifications((prev) => {
        const exists = prev.some(
          (n) =>
            n._id === notification._id ||
            (n.data?.outsourceCaseId === notification.data?.outsourceCaseId &&
              n.type === notification.type &&
              n.type === 'file_uploaded' &&
              n.data?.fileId === notification.data?.fileId)
        );
        if (exists) return prev;
        return [notification, ...prev];
      });

      setUnreadCount((prev) => prev + 1);
      showToast(notification);

      if (Notification.permission === 'granted') {
        try {
          new Notification(notification.title || 'New Notification', {
            body: notification.message || '',
            icon: '/favicon.ico',
            silent: false
          });
        } catch {}
      }

      try {
        const audio = new Audio('/notification.mp3');
        audio.play().catch(() => {});
      } catch {}
    });

    // ============ NEW: CHAT / MENTION NOTIFICATIONS ============
    newSocket.on('notification:new', (notification) => {
      setNotifications((prev) => {
        if (prev.some((n) => n._id === notification._id)) return prev;
        return [notification, ...prev];
      });
      setUnreadCount((prev) => prev + 1);
      showToast(notification);

      if (Notification.permission === 'granted') {
        try {
          new Notification(notification.title || 'New Notification', {
            body: notification.message || '',
            icon: '/favicon.ico'
          });
        } catch {}
      }
    });

    newSocket.on('notification:read', ({ notificationId }) => {
      setNotifications((prev) =>
        prev.map((n) =>
          n._id === notificationId ? { ...n, read: true, readAt: new Date() } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    });

    // ============ NEW: CHAT MESSAGE BROADCAST ============
    newSocket.on('chat:new-message', (message) => {
      if (!message?.groupId) return;
      const gid = String(message.groupId);

      setChatMessagesByGroup((prev) => {
        const list = prev[gid] || [];
        if (list.some((m) => String(m._id) === String(message._id))) return prev;
        return { ...prev, [gid]: [...list, message] };
      });

      // Don't increment unread for the group if user is actively viewing it
      // (handled by consumer pages via a flag on body)
      setChatUnreadByGroup((prev) => ({
        ...prev,
        [gid]: (prev[gid] || 0) + 1
      }));
    });

    // ============ ✅ NEW: CHAT SEEN UPDATE (read receipts) ============
    newSocket.on('chat:seen-update', ({ groupId, messageIds, seenEntry }) => {
      if (!groupId || !Array.isArray(messageIds) || !seenEntry) return;
      const gid = String(groupId);

      setChatMessagesByGroup((prev) => {
        const list = prev[gid];
        if (!list || !Array.isArray(list)) return prev;

        const updated = list.map((m) => {
          // Only update messages that were marked as seen
          if (!messageIds.some((id) => String(id) === String(m._id))) return m;

          const existingSeen = Array.isArray(m.seenBy) ? m.seenBy : [];

          // Dedupe by userIdObjectId
          if (
            existingSeen.some(
              (s) =>
                String(s.userIdObjectId) === String(seenEntry.userIdObjectId)
            )
          ) {
            return m;
          }

          return { ...m, seenBy: [...existingSeen, seenEntry] };
        });

        return { ...prev, [gid]: updated };
      });
    });

    setSocket(newSocket);

    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }

    return () => {
      newSocket.disconnect();
      socketRef.current = null;
    };
  }, [getUserData, showToast]);

  const markAsRead = useCallback(
    (notificationId) => {
      setNotifications((prev) =>
        prev.map((n) =>
          n._id === notificationId ? { ...n, read: true, readAt: new Date() } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      if (socket) socket.emit('notification-read', { notificationId });
    },
    [socket]
  );

  const clearToast = useCallback(() => setToastNotification(null), []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // ---------- Chat helpers exposed to consumers ----------

  const joinGroupRoom = useCallback(
    (groupId) => {
      return new Promise((resolve) => {
        if (!socketRef.current || !groupId) return resolve({ success: false });
        socketRef.current.emit(
          'chat:join-group',
          { groupId },
          (ack) => resolve(ack || { success: false })
        );
      });
    },
    []
  );

  const leaveGroupRoom = useCallback((groupId) => {
    if (socketRef.current && groupId) {
      socketRef.current.emit('chat:leave-group', { groupId });
    }
  }, []);

  const sendChatMessage = useCallback((groupId, text) => {
    return new Promise((resolve) => {
      if (!socketRef.current || !groupId || !text?.trim()) {
        return resolve({ success: false, message: 'Invalid message' });
      }
      socketRef.current.emit(
        'chat:send-message',
        { groupId, text: text.trim() },
        (ack) => resolve(ack || { success: false })
      );
    });
  }, []);

  const resetGroupUnread = useCallback((groupId) => {
    if (!groupId) return;
    setChatUnreadByGroup((prev) => ({ ...prev, [String(groupId)]: 0 }));
  }, []);

  const setGroupMessages = useCallback((groupId, messages) => {
    if (!groupId) return;
    setChatMessagesByGroup((prev) => ({
      ...prev,
      [String(groupId)]: Array.isArray(messages) ? messages : []
    }));
  }, []);

  // ============ ✅ NEW: Mark group messages as seen ============
  const markGroupMessagesSeen = useCallback((groupId, messageIds = []) => {
    return new Promise((resolve) => {
      if (!socketRef.current || !groupId) {
        return resolve({ success: false });
      }
      socketRef.current.emit(
        'chat:mark-seen',
        { groupId, messageIds },
        (ack) => resolve(ack || { success: false })
      );
    });
  }, []);

  const value = {
    socket,
    isConnected,
    isRegistered,
    notifications,
    unreadCount,
    toastNotification,
    loading,
    setNotifications,
    setUnreadCount,
    markAsRead,
    showToast,
    clearToast,
    clearAllNotifications,

    // Chat
    chatMessagesByGroup,
    chatUnreadByGroup,
    joinGroupRoom,
    leaveGroupRoom,
    sendChatMessage,
    resetGroupUnread,
    setGroupMessages,

    // ✅ NEW: read receipts
    markGroupMessagesSeen
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

export default SocketContext;